import * as React from 'react';
import {
  faItalic,
  faListUl,
  faArrowToBottom,
  faArrowToTop,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faText,
} from '@fortawesome/pro-solid-svg-icons';
import {i18n} from '~/i18n';
import {fontWeights, getBoldKey} from '../definitions/font-weights';
import {getSizeIndex, sizeOptions} from './size-options';
import {useSelector} from 'react-redux';
import {FontLoader} from '@snapper/core';
import {
  selectLazyEditorFonts,
  selectLazyPlayerFonts,
  selectTextElementFieldFontOptions,
} from '~/stores/player/selectors/theme.selectors';
import {DEFAULT_FONT_TXT} from '~/utils/theme.constants';
import {toolbarLinkEdit} from './link-edit';

export const PlayerFontsLoader = React.memo(() => {
  const playerFonts = useSelector(selectLazyPlayerFonts);

  return !!playerFonts?.length && <FontLoader fonts={playerFonts} />;
});

const EditorFontsLoader = React.memo(() => {
  const editorFonts = useSelector(selectLazyEditorFonts);

  return !!editorFonts?.length && <FontLoader fonts={editorFonts} />;
});

const textAlign = {
  left: {label: i18n('text-align.left'), icon: faAlignLeft},
  center: {label: i18n('text-align.center'), icon: faAlignCenter},
  right: {label: i18n('text-align.right'), icon: faAlignRight},
};

const renderFontOption = ({text, value: {name, fontFamily} = {}, defaultValue}) => {
  let val = text || name;
  let family = fontFamily || name;

  if (val === 'Standard') {
    val = defaultValue?.name || DEFAULT_FONT_TXT;
    family = defaultValue?.fontFamily || DEFAULT_FONT_TXT;
  }

  return <span style={{fontFamily: family || 'inherit'}}>{val}</span>;
};

const getFontOptions = ({
  settings: {fonts = []} = {},
  config,
}) => {
  const configFonts = config?.getProperty('fonts') || [];

  return [...fonts, ...configFonts].map(value => ({
    text: value.label,
    value: {name: value.label, fontFamily: value.value},
  }));
};

const toolbarFont = {
  icon: faText,
  tooltip: i18n('toolbar.text'),
  title: i18n('toolbar.text'),
  type: 'menu',
  fields: [
    {
      name: 'font',
      label: i18n('toolbar.font'),
      type: 'select',
      subtitle: i18n('toolbar.select-font'),
      options: getFontOptions,
      optionsSelector: selectTextElementFieldFontOptions,
      renderOption: renderFontOption,
      defaultValue: (field, page) => ({name: page.defaultFont, fontFamily: page.defaultFont}),
      LoaderComponent: EditorFontsLoader,
    },
    {
      name: 'color',
      label: i18n('toolbar.text-color'),
      type: 'color',
      defaultValue: null,
    },
    {
      name: 'size',
      type: 'range',
      step: 1,
      minValue: 0,
      maxValue: sizeOptions.length - 1,
      defaultValue: 1,
      getValue: value => getSizeIndex(value),
      setValue: index => sizeOptions[Math.min(Math.max(index, 0), sizeOptions.length - 1)]?.value,
      showButtons: true,
    },
  ],
};

export const commonTextElementFields = [
  toolbarFont,
  {
    icon: ({value}) => (textAlign[value] || textAlign.left).icon,
    tooltip: ({value}) => (textAlign[value] || textAlign.left).label,
    name: 'align',
    type: 'button',
    defaultValue: 'left',
    onClick: ({value, setValue}) => {
      let indexValue = Object.keys(textAlign).indexOf(value) + 1;

      if (indexValue >= Object.keys(textAlign).length) {
        indexValue = 0;
      }
      setValue(Object.keys(textAlign)[indexValue]);
    },
  },
  {
    visible: ({dynamicLayout}) => !dynamicLayout,
    icon: ({value}) => value === 'bottom' ? faArrowToBottom : faArrowToTop,
    name: 'verticalAlign',
    type: 'button',
    defaultValue: 'top',
    onClick: ({value, setValue}) => setValue(value === 'bottom' ? 'top' : 'bottom'),
  },
  {
    icon: ({value}) => (fontWeights[getBoldKey(value)] || fontWeights[false]).icon,
    tooltip: ({value}) => (fontWeights[getBoldKey(value)] || fontWeights[false]).label,
    name: 'bold',
    type: 'button',
    defaultValue: false,
    onClick: ({value, setValue}) => {
      let indexValue = Math.max(Object.keys(fontWeights).indexOf(getBoldKey(value)), 0) + 1;

      if (indexValue >= Object.keys(fontWeights).length) {
        indexValue = 0;
      }
      setValue(Object.keys(fontWeights)[indexValue]);
    },
  },
  {
    name: 'italic',
    icon: faItalic,
    tooltip: i18n('toolbar.italic'),
    type: 'boolean',
  },
  toolbarLinkEdit,
  {
    name: 'unorderedList',
    icon: faListUl,
    visible: ({item: {id} = {}, isEditing}) => !isEditing(id), // Cannot be changed while editing.
    tooltip: i18n('toolbar.bullet-list'),
    type: 'boolean',
  },
  // aiToolbarField,
];
